// @givmo/employer-module — the Reports page's tiles (EP-WEB26; the exports are EP-WEB7's).
//
// The top of the page is the reports themselves, five tiles. Tax documents leads (EP-WEB26, Jonathan's
// ruling 2026-09-29, Q4) and holds both of a company's tax artifacts (Q1): the annual reports (its
// primary action) and the tax lines spreadsheet for a chosen year (a second action in its own zone;
// `tax-documents-tile.tsx`). Then the year recap (leadership's story), Impact and Enrollment (the
// program), and Transactions (finance). The exports keep their descriptions word for word (Q2).
// Every tile works:
//
//   - the four CSV exports DOWNLOAD a real file (the four-CSV floor, unchanged in what it fetches and
//     saves — `server-exports.ts`, `enrollment-export.ts`); the tax lines from the Tax documents tile;
//   - the recap tile VIEWS the recap further down this page;
//   - the Tax documents tile VIEWS the annual-report section further down (the portal), or OPENS the
//     Organization's own Tax documents page (the company console — the host's address book says which).
//
// The rule the page has kept since Wave 0 still governs: "A money product does not ship decorative
// controls (§5), and an empty promise is exactly that." No tile is disabled-with-a-tooltip, and none
// promises a report that does not exist.
//
// ══ EACH EXPORT STATES ITS OWN OUTCOME ═══════════════════════════════════════════════════════════
//
// A tile runs one export at a time (a second click while it prepares does nothing) and says how the
// last attempt ended, on the tile itself, beside the toast that announces it:
//   preparing → "Preparing…";  saved → "Downloaded" (briefly);
//   nothing to save (an empty roster) → "No employees to export yet.", on the tile, until the next attempt;
//   failed → "Not downloaded." and the reason, on the tile, until the next attempt; the action says "Try again";
//   ready → a finished file the browser would have held: "Ready — Save", and one click saves it.
//
// Tiles PREPARE independently (Q6): preparing one never blocks another, because each is a separate
// read-only GET. They cannot SAVE independently, because a browser allows one save per click: a file
// that finishes just after another was saved, with no click between, is held behind the browser's
// "Download multiple files" prompt, and its tile would have said "Downloaded" over a file that never
// arrived. So such a file waits on its tile, saying "Ready — Save" and announcing nothing yet, until the
// person's click saves it (`csv-export.ts` § One save per interaction; EP-WEB26, Jonathan's ruling
// 2026-09-29 on the review's F1).
//
// The toasts are EP-WEB7's, word for word: a SERVER-rendered export's row count is decided by the
// server and never told to the client, so its success says only that the file downloaded — "Exported
// 0 impact" over a file listing 900 charities is exactly the confident wrong number that ends up in a
// board deck. Only the client-built Enrollment export reports a count, because it counted the rows.
//
// The `id` on the section is the anchor the dashboard's Export menu targets
// (`{ to: paths.reports, hash: 'exports' }`); without it that menu item lands silently at page top.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { donationsListCompanyStatsReportsOptions } from '@givmo/api-client/query';
import { extractList } from '@givmo/api-client/list';
import type { StatisticsReportOut } from '@givmo/api-client';
import { Icon, cn, testIdProp, toast } from '@givmo/design-system';
import {
  ArrowLeftRight,
  Check,
  Download,
  HeartHandshake,
  LoaderCircle,
  Presentation,
  RotateCcw,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { taxDocumentsSummary } from '../documents/documents-decode';
import { blobSave, saveTurnIsFree, type SaveFile } from './csv-export';
import { downloadEnrollmentCsv } from './enrollment-export';
import {
  ExportStatusLine,
  ReportTile,
  TILE_ACTION_CLASS,
  ViewAction,
  exportActionName,
  type ExportState,
} from './report-tile';
import { TaxDocumentsTile } from './tax-documents-tile';
import {
  defaultTransactionsWindow,
  downloadImpactCsv,
  downloadTaxLinesCsv,
  downloadTransactionsCsv,
  exportErrorMessage,
} from './server-exports';

const PREFIX = 'exports';

/** How many tax years the picker offers. Older history is a support request, not a self-serve export. */
const TAX_YEAR_SPAN = 5;

/** How long a finished export keeps saying so before its tile returns to "Download". */
const SETTLED_MS = 4000;

/** Where the page's own sections are, for the tiles that VIEW them. */
export const RECAP_ANCHOR = 'recap';
export const TAX_DOCUMENTS_ANCHOR = 'tax-documents';

type ExportId = 'impact' | 'enrollment' | 'transactions' | 'tax';

const IDLE: ExportState = { kind: 'idle' };

/** A finished file waiting on its tile for the person's click, and the toast that will announce it. */
interface HeldFile {
  blob: Blob;
  filename: string;
  announce: string;
}

/**
 * One state per export tile, the run that moves it, and the click that saves a held file.
 *
 * `task` receives the `save` its file must go through and resolves to a row count for the
 * CLIENT-built export, or void for a server-rendered one — because the server decides those row
 * counts and we do not have them.
 */
function useExportRuns() {
  const [states, setStates] = useState<Partial<Record<ExportId, ExportState>>>({});
  // In flight, synchronously: two clicks inside one frame must not start two runs.
  const inFlight = useRef(new Set<ExportId>());
  const timers = useRef(new Map<ExportId, ReturnType<typeof setTimeout>>());
  // Files the browser would have held, each waiting on its own tile.
  const held = useRef(new Map<ExportId, HeldFile>());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const set = useCallback((id: ExportId, state: ExportState) => {
    setStates((current) => ({ ...current, [id]: state }));
  }, []);

  /** A file reached the person's downloads: "Downloaded" for a moment, then "Download" again. */
  const settle = useCallback(
    (id: ExportId) => {
      set(id, { kind: 'saved' });
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          set(id, IDLE);
        }, SETTLED_MS),
      );
    },
    [set],
  );

  const run = useCallback(
    async (id: ExportId, task: (save: SaveFile) => Promise<number | void>, noun: string) => {
      if (inFlight.current.has(id)) return;
      inFlight.current.add(id);
      const pending = timers.current.get(id);
      if (pending !== undefined) clearTimeout(pending);
      // A new run replaces a file still waiting for its click.
      held.current.delete(id);
      set(id, { kind: 'preparing' });
      // Saved now while the browser's turn is free; otherwise kept for the person's click.
      const outcome: { held: { blob: Blob; filename: string } | null } = { held: null };
      const save: SaveFile = (blob, filename) => {
        if (saveTurnIsFree()) blobSave(blob, filename);
        else outcome.held = { blob, filename };
      };
      try {
        const rows = await task(save);
        if (typeof rows === 'number' && rows === 0) {
          // Nothing was saved, and that stays true until the data changes: the tile keeps saying so
          // (a check mark here would read as a file that arrived).
          const nothing = `No ${noun} to export yet`;
          toast.success(nothing);
          set(id, { kind: 'nothing', message: `${nothing}.` });
          return;
        }
        const announce =
          typeof rows === 'number' ? `Exported ${rows} ${noun}` : 'Export downloaded';
        if (outcome.held !== null) {
          // Nothing has reached the person's downloads yet, so nothing is announced yet.
          held.current.set(id, { ...outcome.held, announce });
          set(id, { kind: 'ready' });
          return;
        }
        toast.success(announce);
        settle(id);
      } catch (error) {
        const message = exportErrorMessage(error, noun);
        toast.error(message);
        // The failure stays on the tile until the next attempt: a toast is gone in seconds, and an
        // admin who looked away must still be able to see that the file never arrived, and why.
        set(id, { kind: 'failed', message });
      } finally {
        inFlight.current.delete(id);
      }
    },
    [set, settle],
  );

  /** Save a held file. The click that calls this gave the browser's turn back. */
  const saveHeld = useCallback(
    (id: ExportId) => {
      const file = held.current.get(id);
      if (file === undefined) return;
      held.current.delete(id);
      blobSave(file.blob, file.filename);
      toast.success(file.announce);
      settle(id);
    },
    [settle],
  );

  /** Drop a held file that no longer matches its tile (the tax year was changed). */
  const discard = useCallback(
    (id: ExportId) => {
      if (!held.current.delete(id)) return;
      set(id, IDLE);
    },
    [set],
  );

  return { state: (id: ExportId) => states[id] ?? IDLE, run, saveHeld, discard };
}

/** What the action says, and which glyph it carries, in each state. */
const ACTION_LOOK: Record<ExportState['kind'], { label: string; glyph: LucideIcon; tone: string }> =
  {
    idle: { label: 'Download', glyph: Download, tone: 'text-brand-600 group-hover:text-brand-700' },
    preparing: { label: 'Preparing…', glyph: LoaderCircle, tone: 'text-text-secondary' },
    ready: {
      label: 'Ready — Save',
      glyph: Download,
      tone: 'text-brand-600 group-hover:text-brand-700',
    },
    saved: { label: 'Downloaded', glyph: Check, tone: 'text-success-700' },
    nothing: {
      label: 'Download',
      glyph: Download,
      tone: 'text-brand-600 group-hover:text-brand-700',
    },
    failed: {
      label: 'Try again',
      glyph: RotateCcw,
      tone: 'text-brand-600 group-hover:text-brand-700',
    },
  };

interface ExportTileProps {
  id: ExportId;
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  /** The period (or the year picker). */
  meta: ReactNode;
  state: ExportState;
  onRun: () => void;
  /** Saves the file waiting on this tile (state `ready`). */
  onSave: () => void;
}

function ExportTile({ id, icon, title, description, meta, state, onRun, onSave }: ExportTileProps) {
  const statusId = useId();
  const look = ACTION_LOOK[state.kind];
  const stated = state.kind === 'failed' || state.kind === 'nothing';
  return (
    <ReportTile
      testId={`${PREFIX}-row-${id}`}
      icon={icon}
      title={title}
      description={description}
      meta={meta}
      busy={state.kind === 'preparing'}
      status={<ExportStatusLine state={state} id={statusId} testId={`${PREFIX}-status-${id}`} />}
      action={
        <button
          type="button"
          onClick={state.kind === 'ready' ? onSave : onRun}
          disabled={state.kind === 'preparing'}
          aria-label={exportActionName(state.kind, `the ${title} CSV`)}
          aria-describedby={stated ? statusId : undefined}
          className={cn(TILE_ACTION_CLASS, look.tone)}
          {...testIdProp(`${PREFIX}-run-${id}`)}
        >
          <Icon
            icon={look.glyph}
            size={16}
            className={
              state.kind === 'preparing' ? 'animate-spin motion-reduce:animate-none' : undefined
            }
            aria-hidden
          />
          {/* A phone row shows the glyph alone, except for a file waiting for its click: that one
              says so at every width, or it would look exactly like an idle tile. */}
          <span className={cn(state.kind !== 'ready' && 'max-sm:sr-only')}>{look.label}</span>
        </button>
      }
    />
  );
}

export function ExportsSection() {
  const { taxDocuments } = useEmployerModulePaths();
  const { state, run, saveHeld, discard } = useExportRuns();
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(String(currentYear));

  const taxYearOptions = useMemo(
    () =>
      Array.from({ length: TAX_YEAR_SPAN }, (_, index) => {
        const year = String(currentYear - index);
        return { value: year, label: year };
      }),
    [currentYear],
  );

  // The annual reports' state, for the Tax documents tile — the same read (and, in the portal, the same
  // cache entry) as the list it points at. Unknown (loading, or a failed read) states the format alone.
  const reports = useQuery({
    ...donationsListCompanyStatsReportsOptions(),
    select: (data: unknown) => extractList<StatisticsReportOut>(data),
  });
  const taxDocumentsState = reports.isSuccess ? taxDocumentsSummary(reports.data) : null;

  // Computed per render, so a tab left open overnight cannot send yesterday's window. The tile shows
  // the SAME window the request carries: a finance export whose displayed period drifts from the sent
  // one invites a reconciliation against the wrong quarter.
  const transactionsWindow = defaultTransactionsWindow();

  return (
    <section
      id="exports"
      aria-label="Reports"
      className="scroll-mt-[88px]"
      {...testIdProp(`${PREFIX}-section`)}
    >
      <ul className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-16 xl:grid-cols-3">
        <TaxDocumentsTile
          door={taxDocuments}
          readiness={taxDocumentsState}
          anchor={TAX_DOCUMENTS_ANCHOR}
          taxYear={taxYear}
          taxYearOptions={taxYearOptions}
          onTaxYearChange={(value) => {
            setTaxYear(value ?? String(currentYear));
            // A held spreadsheet is the year it was prepared for; it is not the newly chosen one.
            discard('tax');
          }}
          csv={state('tax')}
          onRunCsv={() =>
            void run('tax', (save) => downloadTaxLinesCsv(Number(taxYear), save), 'tax')
          }
          onSaveCsv={() => saveHeld('tax')}
        />
        <ReportTile
          testId="reports-tile-recap"
          icon={Presentation}
          title="Year recap"
          description="Your year at a glance, for a leadership update or an all-hands slide."
          meta={`${currentYear} to date`}
          action={
            <ViewAction
              anchor={RECAP_ANCHOR}
              label="View the year recap"
              testId="reports-tile-recap-view"
            />
          }
        />
        <ExportTile
          id="impact"
          icon={HeartHandshake}
          title="Impact"
          description="Every charity your company supported this year, ranked by total — the full list, not just the top few."
          meta={`CSV · ${currentYear} to date`}
          state={state('impact')}
          onRun={() => void run('impact', (save) => downloadImpactCsv(new Date(), save), 'impact')}
          onSave={() => saveHeld('impact')}
        />
        <ExportTile
          id="enrollment"
          icon={Users}
          title="Enrollment"
          description="Every enrolled employee, their work email, and the date they joined your program."
          meta="CSV · Current roster"
          state={state('enrollment')}
          onRun={() => void run('enrollment', (save) => downloadEnrollmentCsv(save), 'employees')}
          onSave={() => saveHeld('enrollment')}
        />
        <ExportTile
          id="transactions"
          icon={ArrowLeftRight}
          title="Transactions"
          // The window is in the DESCRIPTION, not a tooltip: a finance export whose period is invisible
          // is the kind of file that gets reconciled against the wrong quarter.
          description={
            // Word for word as before; each date is kept whole, so a phone never breaks
            // "2026-01-01" at its hyphen.
            <>
              Every money event — deposits, matches, direct gifts, and gift cards — from{' '}
              <span className="whitespace-nowrap">{transactionsWindow.from}</span> to{' '}
              <span className="whitespace-nowrap">{transactionsWindow.to}</span>. Each row states
              which amount basis it uses.
            </>
          }
          meta="CSV"
          state={state('transactions')}
          onRun={() =>
            void run(
              'transactions',
              (save) => downloadTransactionsCsv(transactionsWindow, save),
              'transactions',
            )
          }
          onSave={() => saveHeld('transactions')}
        />
      </ul>
    </section>
  );
}
